import React from 'react';
import { useNavigate } from 'react-router-dom';
import SalaIcon from './SalaIcon.jsx';
import { useSaidaSuave } from '../hooks/useSaidaSuave';

/**
 * PainelPendencias — modal acionado pelos cards de alerta da Home
 * (07/07/2026, reestruturado no mesmo dia). Antes era organizado por
 * nível de urgência (Crítico/Atenção/Informativo) — misturava coisa
 * pessoal (PA atrasado, atribuído a você) com contagem de gestão de
 * sala (EPI vencido, visível só a quem administra aquela sala). Juan
 * pediu pra virar sobre responsabilidade real: o que é seu, o que é da
 * sua equipe (se você supervisiona alguém), e o que é gestão de sala
 * (fica separado, sem quebrar por pessoa — não existe elo entre
 * colaborador e usuário de login pra fazer isso ainda).
 *
 * Cor de cada item ainda indica a urgência (crítico/atenção) — só não é
 * mais o eixo que decide ONDE o item aparece.
 */

const COR_URGENCIA = { critico: 'danger', atencao: 'warning', info: 'info' };

function ItemPendencia({ item, onClick }) {
  return (
    <div className="n-row" onClick={onClick}>
      {/* ⚠ Sem sala não há ícone de sala — e um vazio de 16px alinha
          melhor que um ícone genérico que promete a sala errada. */}
      <span style={{ opacity: .6, flexShrink: 0, marginTop: 1, width: 16, display: 'inline-block' }}>
        {item.sala ? <SalaIcon numero={item.sala} size={16} /> : null}
      </span>
      <div className="n-txt" style={{ flex: 1 }}>
        <strong>{item.titulo}</strong>
        {/* ⚠ O serviço manda `descricao` em uns e `detalhe` em outros — as
            duas chaves existem há tempo. Aceitar as duas é mais honesto que
            padronizar 200 linhas de serviço no meio de outra tarefa. */}
        {(item.descricao || item.detalhe) && <div className="n-time">{item.descricao || item.detalhe}</div>}
        {item.extra && <div className="n-time">{item.extra}</div>}
      </div>
      <div style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, marginTop: 5, background: `var(--${COR_URGENCIA[item.urgencia] || 'neutral'})` }} />
    </div>
  );
}

export default function PainelPendencias({ aberto, dados, onClose }) {
  const navigate = useNavigate();
  const { montado, fechando } = useSaidaSuave(aberto, 200, onClose);
  if (!montado) return null;

  const { minhas = [], equipe = [], gestaoSala = [], compromissosSemana = [] } = dados || {};

  const irPara = (item) => {
    onClose();
    /* ⚠ NEM TODA PENDÊNCIA MORA NUMA SALA (13/09/2026). O centro avulso
       vencendo vive na Administração, e a navegação só sabia de
       `/sala/N` — item sem sala abria o painel e **não ia a lugar
       nenhum**. `rota` é a saída explícita; `sala` continua para os que
       já a usam. */
    if (item.rota) navigate(item.rota);
    else if (item.sala) navigate(`/sala/${item.sala}`);
  };

  const nada = minhas.length === 0 && equipe.length === 0 && gestaoSala.length === 0 && compromissosSemana.length === 0;

  return (
    <div className={`modal-overlay ${fechando ? 'closing' : ''}`} onClick={onClose}>
      <div className={`modal-box ${fechando ? 'closing' : ''}`} style={{ maxWidth: 540, padding: 0 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ padding: '18px 20px 14px', marginBottom: 0, borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 'var(--txt-18)' }}>Minhas Demandas</h3>
          <button className="btn-acao" onClick={onClose} aria-label="Fechar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.71" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4.5" y1="4.5" x2="19.5" y2="19.5" /><line x1="19.5" y1="4.5" x2="4.5" y2="19.5" />
            </svg>
          </button>
        </div>

        <div style={{ maxHeight: 460, overflowY: 'auto' }}>
          {nada && (
            <div className="vazio-box" style={{ border: 'none', padding: '28px 20px' }}>
              Nada pendente no momento — tudo em dia.
            </div>
          )}

          {minhas.length > 0 && (
            <div>
              <div style={{ fontSize: 'var(--txt-11)', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '14px 16px 4px' }}>
                Minhas Demandas
              </div>
              {minhas.map((item, i) => <ItemPendencia key={i} item={item} onClick={() => irPara(item)} />)}
            </div>
          )}

          {equipe.length > 0 && (
            <div>
              <div style={{ fontSize: 'var(--txt-11)', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '14px 16px 4px' }}>
                Minha Equipe
              </div>
              {equipe.map(pessoa => (
                <div key={pessoa.usuario_id}>
                  <div style={{ fontSize: 'var(--txt-13)', fontWeight: 500, color: 'var(--text-primary)', padding: '8px 16px 2px' }}>
                    {pessoa.nome}
                  </div>
                  {pessoa.itens.map((item, i) => <ItemPendencia key={i} item={item} onClick={() => irPara(item)} />)}
                </div>
              ))}
            </div>
          )}

          {gestaoSala.length > 0 && (
            <div>
              <div style={{ fontSize: 'var(--txt-11)', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '14px 16px 4px' }}>
                Gestão da Sala
              </div>
              {gestaoSala.map((item, i) => <ItemPendencia key={i} item={item} onClick={() => irPara(item)} />)}
            </div>
          )}

          {compromissosSemana.length > 0 && (
            <div>
              <div style={{ fontSize: 'var(--txt-11)', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '14px 16px 4px' }}>
                Compromissos da Semana
              </div>
              {compromissosSemana.map((c, i) => (
                <div key={i} className="n-row" onClick={() => { onClose(); navigate('/sala/19'); }}>
                  <div className="n-txt" style={{ flex: 1 }}>
                    <strong>{c.titulo}</strong>
                    <div className="n-time">{c.dataLabel} às {c.hora}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
